import { restoreErrorMessage } from "@/api/archived";
import { useFlowSessionById, useUpdateFlowSession } from "@/api/flow_sessions";
import {
  useAgentCapabilities,
  useHarnessCapabilities,
} from "@/api/harnessCapabilities";
import { pendingSessionModel, trackSessionWrite } from "@/api/sessionWrites";
import { AgentId } from "@/constants/agents";
import { HarnessDraft } from "@/hooks/useHarnessDraft";
import { AgentReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { FlowSession, UpdateFlowSessionRequest } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import toast from "react-hot-toast";
import {
  AgentEffects,
  AgentEvent,
  AgentSendSettings,
  AgentStateInput,
  agentTransition,
  AgentView,
  agentView,
  landedDraft,
} from "../agentState";

export type { HarnessModelOption } from "../agentState";

// A minted session's pick waiting on Connect, keyed by session and module-level so every control
// on a surface — the composer and the agent cards above it — shows and lands the same one.
const heldPicks = new Map<string, HarnessDraft>();
const heldListeners = new Set<() => void>();

function setHeldPick(flowSessionId: string, pick: HarnessDraft | null) {
  if (pick) {
    heldPicks.set(flowSessionId, pick);
  } else {
    heldPicks.delete(flowSessionId);
  }
  heldListeners.forEach((listener) => listener());
}

function subscribeToHeldPicks(listener: () => void) {
  heldListeners.add(listener);
  return () => heldListeners.delete(listener);
}

/** Test seam: the held store outlives component unmounts. */
export function clearPendingAgentPicks() {
  heldPicks.clear();
  heldListeners.forEach((listener) => listener());
}

export interface UseHarnessControlParams {
  /**
   * The stamped session this composer sends into, if any. Until its first message the stamp is
   * unspent and a pick writes through to the row; after that the agent is locked — every later
   * turn runs on it.
   */
  flowSessionId?: string;
  /** The surface's new-chat pick: the choice until there is a session, and what a mint starts on. */
  draft?: HarnessDraft;
  /** Set the draft. Its presence is what makes a session-less control interactive. */
  onDraftChange?: (draft: HarnessDraft) => void;
  /** The host's Cohesive tier, sent until a Cohesive chat has started and has its own. */
  hostTier?: AgentReasoningEffort;
}

export interface HarnessControl extends Omit<AgentView, "sendSettings"> {
  setAgent: (agent: AgentId) => void;
  setModel: (model: string) => void;
  setEffort: (effort: string) => void;
  setTier: (tier: AgentReasoningEffort) => void;
  /**
   * What a message sent now runs on. Stable, and read at call time, so a handler made before a
   * pick still sends it.
   */
  getSendSettings: () => AgentSendSettings;
}

/**
 * The composer's agent, model and thinking, from `agentState`: this hook gathers its inputs (the
 * draft, the session row, the held pick, the catalog, the writes in flight) and applies what a
 * pick or a landed credential writes.
 */
export function useHarnessControl({
  flowSessionId,
  draft,
  onDraftChange,
  hostTier,
}: UseHarnessControlParams): HarnessControl {
  const flags = useFlags<Flags>();
  // Shares the query key used elsewhere, so React Query dedupes — no extra fetch.
  const { data: flowSession } = useFlowSessionById(flowSessionId || "");
  const { data: capabilities } = useHarnessCapabilities();
  const { data: agents, isPending: catalogPending } = useAgentCapabilities();
  const queryClient = useQueryClient();
  const held = useSyncExternalStore(subscribeToHeldPicks, () =>
    flowSessionId ? heldPicks.get(flowSessionId) : undefined,
  );

  const input: AgentStateInput = {
    flowSessionId,
    session: flowSession ?? undefined,
    draft,
    hasDraftOwner: !!onDraftChange,
    held,
    agents,
    capabilities,
    harnessRuntime: !!flags.harnessRuntime,
    pendingModel: pendingSessionModel(flowSessionId),
    hostTier,
    catalogPending,
  };
  const view = agentView(input);
  const latest = useRef(input);
  latest.current = input;

  const updateFlowSession = useUpdateFlowSession({
    onSuccess: (data) => {
      // Merged from the server's row, not the request: the response is what actually landed,
      // and the explicit keys carry an omitempty-cleared model over the cached one.
      queryClient.setQueryData<FlowSession>(["flowSession", data.id], (prev) =>
        prev
          ? {
              ...prev,
              harness: data.harness,
              model: data.model,
              reasoning_effort: data.reasoning_effort,
            }
          : prev,
      );
      // Only once landed, so a refused write cannot seed a choice the session never took.
      const next = landedDraft(data);
      if (next) onDraftChange?.(next);
    },
    onError: (err, variables) => {
      toast.error(restoreErrorMessage(err, "Failed to update chat"));
      // Resync: a pick shown ahead of the write must not outlive its refusal, and a 422 means the
      // stamp was spent under us (a first message landed).
      queryClient.invalidateQueries({
        queryKey: ["flowSession", variables.flowSessionId],
      });
    },
  });

  // Tracked before it shows, so a message posted into the session waits for it and a send made
  // meanwhile carries its model; then shown at once on every host reading the row.
  const writeThrough = (id: string, data: UpdateFlowSessionRequest) => {
    trackSessionWrite(
      id,
      updateFlowSession.mutateAsync({ flowSessionId: id, data }),
      data.harness ? undefined : data.model,
    );
    queryClient.setQueryData<FlowSession>(["flowSession", id], (prev) =>
      prev
        ? {
            ...prev,
            // An agent change takes its model and thinking with it, as the server does.
            ...(data.harness
              ? {
                  harness: data.harness,
                  model: data.model,
                  reasoning_effort: data.reasoning_effort,
                }
              : {}),
            ...(!data.harness && data.model ? { model: data.model } : {}),
            ...(!data.harness && data.reasoning_effort !== undefined
              ? { reasoning_effort: data.reasoning_effort }
              : {}),
          }
        : prev,
    );
  };

  const apply = (id: string | undefined, effects: AgentEffects) => {
    if (id && effects.held !== undefined) setHeldPick(id, effects.held);
    if (effects.draft) onDraftChange?.(effects.draft);
    if (id && effects.write) writeThrough(id, effects.write);
  };
  const dispatch = (event: AgentEvent) =>
    apply(flowSessionId, agentTransition(input, event));

  // Adopt a minted session's held pick, and land it once its subscription connects. Read fresh,
  // not from the render: the first control here to land it clears it, so the rest find nothing.
  useEffect(() => {
    if (!flowSessionId) return;
    const now = {
      ...latest.current,
      held: heldPicks.get(flowSessionId),
      session:
        queryClient.getQueryData<FlowSession>(["flowSession", flowSessionId]) ??
        latest.current.session,
    };
    apply(flowSessionId, agentTransition(now, { type: "sync" }));
    // The effect keys on what moves the held pick; apply reads the latest render's handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowSessionId, view.phase, view.held, view.connected]);

  const getSendSettings = useCallback(() => {
    const now = latest.current;
    return agentView({
      ...now,
      pendingModel: pendingSessionModel(now.flowSessionId),
    }).sendSettings;
  }, []);

  const { sendSettings: _sendSettings, ...state } = view;
  return {
    ...state,
    setAgent: (agent) => dispatch({ type: "agent", agent }),
    setModel: (model) => dispatch({ type: "model", model }),
    setEffort: (effort) => dispatch({ type: "effort", effort }),
    setTier: (tier) => dispatch({ type: "tier", tier }),
    getSendSettings,
  };
}
